import type { SupportedLanguage } from './language';
import enSEO from '../seo/en';
import zhSEO from '../seo/zh';
import jaSEO from '../seo/ja';
import hiSEO from '../seo/hi';
import idSEO from '../seo/id';
import esSEO from '../seo/es';
import arSEO from '../seo/ar';
import msSEO from '../seo/ms';
import frSEO from '../seo/fr';
import bnSEO from '../seo/bn';
import ruSEO from '../seo/ru';
import ptSEO from '../seo/pt';
import deSEO from '../seo/de';
import urSEO from '../seo/ur';
import paSEO from '../seo/pa';
import faSEO from '../seo/fa';
import swSEO from '../seo/sw';

// SEO configuration mapping
const seoConfigs = {
  en: enSEO,
  zh: zhSEO,
  ja: jaSEO,
  hi: hiSEO,
  id: idSEO,
  es: esSEO,
  ar: arSEO,
  ms: msSEO,
  fr: frSEO,
  bn: bnSEO,
  ru: ruSEO,
  pt: ptSEO,
  de: deSEO,
  ur: urSEO,
  pa: paSEO,
  fa: faSEO,
  sw: swSEO
};

export type SEOConfig = {
  title: string;
  description: string;
  keywords: string[];
  ogTitle: string;
  ogDescription: string;
  jsonLd?: Record<string, any>;
};

/**
 * Get SEO configuration for a specific page and language
 */
export function getSEOConfig(
  page: keyof typeof enSEO,
  language: SupportedLanguage,
  params?: any
): SEOConfig {
  const config = seoConfigs[language] as typeof enSEO | undefined;

  if (!config || !config[page]) {
    // Fallback to English if translation not available
    const fallbackConfig = seoConfigs.en[page];
    if (typeof fallbackConfig === 'function') {
      return fallbackConfig(params);
    }
    return fallbackConfig;
  }

  const pageConfig = config[page];
  if (typeof pageConfig === 'function') {
    return pageConfig(params);
  }

  return pageConfig;
}

/**
 * Update document meta tags with SEO data
 */
export function updateSEOTags(seoConfig: SEOConfig, language: SupportedLanguage): void {
  // Update title
  document.title = seoConfig.title;
  
  // Update or create meta tags
  updateMetaTag('description', seoConfig.description);
  updateMetaTag('keywords', seoConfig.keywords.join(', '));
  updateMetaTag('og:title', seoConfig.ogTitle, 'property');
  updateMetaTag('og:description', seoConfig.ogDescription, 'property');
  updateMetaTag('og:type', 'website', 'property');
  updateMetaTag('og:url', window.location.href, 'property');
  
  // Update language meta tag
  updateMetaTag('language', language);
  document.documentElement.lang = language;
  
  // Update JSON-LD structured data
  if (seoConfig.jsonLd) {
    updateJSONLD(seoConfig.jsonLd);
  }
  
  // Update hreflang tags
  updateHreflangTags();
}

/**
 * Update or create a meta tag
 */
function updateMetaTag(name: string, content: string, attribute: string = 'name'): void {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}

/**
 * Update JSON-LD structured data
 */
function updateJSONLD(jsonLd: Record<string, any>): void {
  // Remove existing JSON-LD
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Add new JSON-LD
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(jsonLd);
  document.head.appendChild(script);
}

/**
 * Update hreflang tags for international SEO
 */
function updateHreflangTags(): void {
  // Remove existing hreflang tags
  const existingHreflang = document.querySelectorAll('link[rel="alternate"][hreflang]');
  existingHreflang.forEach(link => link.remove());
  
  const baseUrl = window.location.origin;
  const pathname = window.location.pathname;
  
  // Add hreflang for each supported language
  const languages: Array<{ code: SupportedLanguage; hreflang: string }> = [
    { code: 'en', hreflang: 'en' },
    { code: 'zh', hreflang: 'zh' },
    { code: 'ja', hreflang: 'ja' },
    { code: 'hi', hreflang: 'hi' },
    { code: 'id', hreflang: 'id' },
    { code: 'es', hreflang: 'es' },
    { code: 'ar', hreflang: 'ar' },
    { code: 'ms', hreflang: 'ms' },
    { code: 'fr', hreflang: 'fr' },
    { code: 'bn', hreflang: 'bn' },
    { code: 'ru', hreflang: 'ru' },
    { code: 'pt', hreflang: 'pt' },
    { code: 'de', hreflang: 'de' },
    { code: 'ur', hreflang: 'ur' },
    { code: 'pa', hreflang: 'pa' },
    { code: 'fa', hreflang: 'fa' },
    { code: 'sw', hreflang: 'sw' }
  ];
  
  languages.forEach(({ code, hreflang }) => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = hreflang;
    
    // For now, using query parameter. In future, could use subdirectory routing
    const url = new URL(pathname, baseUrl);
    if (code !== 'en') {
      url.searchParams.set('lang', code);
    }
    link.href = url.toString();
    
    document.head.appendChild(link);
  });
  
  // Add x-default hreflang
  const defaultLink = document.createElement('link');
  defaultLink.rel = 'alternate';
  defaultLink.hreflang = 'x-default';
  defaultLink.href = baseUrl + pathname;
  document.head.appendChild(defaultLink);
}

/**
 * Generate canonical URL for the current page
 */
export function getCanonicalUrl(): string {
  // Remove language parameter for canonical URL
  const url = new URL(window.location.href);
  url.searchParams.delete('lang');
  return url.toString();
}

/**
 * Update canonical link
 */
export function updateCanonicalLink(): void {
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  
  canonical.href = getCanonicalUrl();
}